import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Beaker } from 'lucide-react';
import { foodProfiles } from '../data/foodProfiles';

const SupplementCard = ({ supplement }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get related food profile names
  const getRelatedFoodNames = () => {
    if (!supplement.relatedFoods || supplement.relatedFoods.length === 0) {
      return [];
    }
    return supplement.relatedFoods
      .map(foodId => {
        const food = foodProfiles.find(f => f.id === foodId);
        return food ? { id: foodId, name: food.name } : null;
      })
      .filter(Boolean);
  };

  const relatedFoods = getRelatedFoodNames();

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors rounded-xl"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-semibold text-slate-900">
              {supplement.name}
            </h3>
            {supplement.hasResearchPage && (
              <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                <Beaker className="h-3 w-3 mr-1" />
                CLL Research
              </span>
            )}
          </div>
          <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-full">
            {supplement.category}
          </span>
        </div>
        <div className="ml-4">
          {isExpanded ? (
            <ChevronUp className="h-6 w-6 text-slate-400" />
          ) : (
            <ChevronDown className="h-6 w-6 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-slate-100 pt-6">
          {/* Research Page Link - if available */}
          {supplement.hasResearchPage && supplement.researchPageUrl && (
            <Link
              to={supplement.researchPageUrl}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl hover:from-green-100 hover:to-emerald-100 transition-colors"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <Beaker className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-green-900">View CLL Research</h4>
                <p className="text-sm text-green-700">Mayo Clinic clinical trial findings for CLL patients</p>
              </div>
              <ChevronDown className="h-5 w-5 text-green-600 rotate-[-90deg]" />
            </Link>
          )}

          {/* Why People Include This Supplement */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Why People Include This Supplement
            </h4>
            <p className="text-slate-700 leading-relaxed">
              {supplement.whyIncluded}
            </p>
          </div>

          {/* General Notes */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              General Notes
            </h4>
            <p className="text-slate-700 leading-relaxed">
              {supplement.generalNotes}
            </p>
          </div>

          {/* Cautions & Considerations */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-red-900 uppercase tracking-wide mb-3">
              Cautions & Considerations
            </h4>
            <p className="text-sm text-red-900 leading-relaxed">
              {supplement.cautions}
            </p>
          </div>

          {/* Related Foods */}
          {relatedFoods.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Related Food Profiles
              </h4>
              <div className="flex flex-wrap gap-2">
                {relatedFoods.map((food) => (
                  <Link
                    key={food.id}
                    to="/integrative-health/food-nutrition"
                    className="inline-flex items-center px-3 py-1.5 bg-slate-50 hover:bg-blue-50 text-slate-700 hover:text-blue-700 text-sm rounded-full transition-colors border border-slate-200 hover:border-blue-200"
                  >
                    {food.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              This information is provided for general educational purposes only and does not constitute medical advice.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplementCard;
